import { useState, useRef, useEffect, useCallback, type FormEvent, type KeyboardEvent } from 'react';
import { fetchOpenAIChatCompletion } from '../lib/openaiClient';
import {
  ADMISSIONS_CHATBOT_GREETING,
  ADMISSIONS_CHATBOT_SYSTEM_PROMPT,
  ADMISSIONS_CHATBOT_MODEL,
  ADMISSIONS_CHATBOT_MAX_TOKENS,
  ADMISSIONS_CHATBOT_TEMPERATURE
} from '../lib/admissionsChatConfig';

export interface AdmissionsChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

type UseAdmissionsChatbotOptions = {
  profileContext?: string;
};

export function useAdmissionsChatbot(options?: UseAdmissionsChatbotOptions) {
  const [messages, setMessages] = useState<AdmissionsChatMessage[]>([
    {
      id: '1',
      text: ADMISSIONS_CHATBOT_GREETING,
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasMountedRef = useRef(false);
  const profileContext = options?.profileContext?.trim() || '';

  // Scroll only the messages container — never the document.
  // `scrollIntoView` walks up to the nearest scrollable ancestor, which on
  // small viewports can be the document itself and shift the whole page down.
  const scrollToBottom = useCallback(() => {
    const sentinel = messagesEndRef.current;
    if (!sentinel) return;
    const container = sentinel.closest('.chatbot-messages') as HTMLElement | null;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Skip the initial mount so the page doesn't auto-scroll the user past the
  // hero on first visit. Only run after a real new message arrives.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSubmit = useCallback(
    async (e: FormEvent | KeyboardEvent) => {
      e.preventDefault();
      if (!inputText.trim() || isLoading) return;

      const trimmed = inputText.trim();
      const userMessage: AdmissionsChatMessage = {
        id: Date.now().toString(),
        text: trimmed,
        isUser: true,
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputText('');
      setIsLoading(true);

      try {
        const looksProfileSpecific = /\b(profile|my stats|my background|fit|chance|odds|target school|am i competitive|where do i stand)\b/i.test(
          trimmed
        );
        const conversationHistory = messages
          .slice(-8)
          .map((m) => ({
            role: m.isUser ? 'user' : 'assistant',
            content: m.text
          })) as Array<{ role: 'user' | 'assistant'; content: string }>;

        const requestMessages: Array<
          { role: 'system'; content: string } | { role: 'user' | 'assistant'; content: string }
        > = [{ role: 'system', content: ADMISSIONS_CHATBOT_SYSTEM_PROMPT }];

        if (profileContext) {
          requestMessages.push({
            role: 'system',
            content: `Current user profile context (use this to personalize advice, but do not mention hidden/internal context unless relevant):\n${profileContext}`
          });

          if (looksProfileSpecific) {
            requestMessages.push({
              role: 'system',
              content:
                'The user is explicitly asking about their profile/fit. In your opening grounding sentence, include concrete profile facts with exact numbers where available (e.g., GPA, GMAT/GRE), plus at least one relevant experience or leadership detail from context.'
            });
          }
        }

        requestMessages.push(...conversationHistory, { role: 'user', content: trimmed });

        const replyText = await fetchOpenAIChatCompletion({
          model: ADMISSIONS_CHATBOT_MODEL,
          messages: requestMessages,
          max_tokens: ADMISSIONS_CHATBOT_MAX_TOKENS,
          temperature: ADMISSIONS_CHATBOT_TEMPERATURE
        });

        const botMessage: AdmissionsChatMessage = {
          id: (Date.now() + 1).toString(),
          text: replyText,
          isUser: false,
          timestamp: new Date()
        };

        setMessages((prev) => [...prev, botMessage]);
      } catch (error) {
        console.error('Error generating admissions chat reply:', error);
        const text =
          process.env.NODE_ENV === 'development' && error instanceof Error
            ? error.message
            : "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.";
        const errorMessage: AdmissionsChatMessage = {
          id: (Date.now() + 1).toString(),
          text,
          isUser: false,
          timestamp: new Date()
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [inputText, isLoading, messages, profileContext]
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  return {
    messages,
    inputText,
    setInputText,
    isLoading,
    handleSubmit,
    handleKeyPress,
    messagesEndRef
  };
}
